import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Popover,
  Stack,
  TextField,
  Autocomplete,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  IconButton
} from '@mui/material';

import { PurchaseOrderFilterCriteria } from '@features/purchasing/hooks/usePurchaseOrderFilter';
import { JSEditableInput } from '../common';
import { FilterList } from '@mui/icons-material';


interface PurchaseOrderFilterProps {
  onFilterChange: (criteria: PurchaseOrderFilterCriteria) => void;
  initialCriteria?: PurchaseOrderFilterCriteria;
  buttonLabel?: string;
  purchaseOrders?: any[];
  products?: any[];
  shipments?: any[];
  suppliers?: any[];
}

const PurchaseOrderFilter: React.FC<PurchaseOrderFilterProps> = ({
  onFilterChange,
  initialCriteria,
  buttonLabel,
  purchaseOrders = [],
  products = [],
  shipments = [],
  suppliers = []
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filterCriteria, setFilterCriteria] = useState<PurchaseOrderFilterCriteria>(initialCriteria || {});
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };


  const handleItemFilterChange = (field: keyof PurchaseOrderFilterCriteria, value: string) => {
    const newCriteria = {
      ...filterCriteria,
      [field]: value
    };
    setFilterCriteria(newCriteria);
    onFilterChange(newCriteria);
  };


  const handleAvailableDaysAgoChange = (value: string) => {
    const valueNumber = value ? Number(value) : undefined;
    const newCriteria = {
      ...filterCriteria,
      availableDaysAgo: valueNumber
    };
    setFilterCriteria(newCriteria);
    onFilterChange(newCriteria);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'purchase-order-filter-popover' : undefined;

  return (
    <div>
      <IconButton
        color="info"
        onClick={handleClick}
      >
        <FilterList />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper sx={{ p: 0, maxWidth: 400 }}>
          <Box sx={{ maxHeight: '500px', maxWidth: '400px', overflowY: 'auto' }}>
            <Stack gap={3} sx={{ p: 2 }}>

              <Stack gap={1}>
                <Typography variant="body2">Purchase Order</Typography>

                {/* Purchase Order */}
                <Autocomplete
                  options={purchaseOrders}
                  getOptionLabel={(option) => {
                    const orderDate = option?.orderDate ? new Date(option.orderDate).toLocaleDateString() : '';
                    const poNumber = option?.poNumber || 'No PO Number';
                    return `${poNumber}${orderDate ? ` (${orderDate})` : ''}`;
                  }}
                  value={purchaseOrders.find(po => po?.id === filterCriteria.purchaseOrderId) || null}
                  onChange={(_, newValue) => {
                    handleItemFilterChange('purchaseOrderId', newValue?.id || undefined);
                  }}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option => {
                      const orderDate = option?.orderDate ? new Date(option.orderDate).toLocaleDateString() : '';
                      const poNumber = option?.poNumber || 'No PO Number';
                      const searchText = `${poNumber} ${option?.id || ''} ${orderDate}`.toLowerCase();
                      return searchText.includes(inputValue.toLowerCase());
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}

                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                />
              </Stack>

              <Stack gap={1}>
                <Typography variant="body2">Product</Typography>

                {/* Product */}
                <Autocomplete
                  options={products}
                  getOptionLabel={(option) => option?.title || option?.id || ''}
                  value={products.find(p => p?.id === filterCriteria.productId) || null}
                  onChange={(_, newValue) => {
                    const newCriteria = {
                      ...filterCriteria,
                      productId: newValue?.id || undefined
                    };
                    setFilterCriteria(newCriteria);
                    onFilterChange(newCriteria);
                  }}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option =>
                      option?.title?.toLowerCase().includes(inputValue.toLowerCase()) ||
                      option?.id?.toLowerCase().includes(inputValue.toLowerCase())
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                />

              </Stack>

              <Stack gap={1}>
                <Typography variant="body2">Shipment</Typography>

                {/* Shipment */}
                <Autocomplete
                  options={shipments}
                  getOptionLabel={(option) => {
                    const processedDate = option?.processedDate ? new Date(option.processedDate).toLocaleDateString() : '';
                    const shipmentName = option?.name || 'No Shipment Name';
                    return `${shipmentName}${processedDate ? ` (${processedDate})` : ''}`;
                  }}
                  value={shipments.find(s => s?.id === filterCriteria.shipmentId) || null}
                  onChange={(_, newValue) => {
                    handleItemFilterChange('shipmentId', newValue?.id || undefined);
                  }}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option => {
                      const processedDate = option?.processedDate ? new Date(option.processedDate).toLocaleDateString() : '';
                      const shipmentName = option?.name || 'No Shipment Name';
                      const searchText = `${shipmentName} ${option?.id || ''} ${processedDate}`.toLowerCase();
                      return searchText.includes(inputValue.toLowerCase());
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}

                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,

                      }}
                    />
                  )}
                />
              </Stack>

              <Stack gap={1}>
                <Typography variant="body2">Supplier</Typography>

                <Autocomplete
                  options={suppliers}
                  getOptionLabel={(option) => option?.name || option?.id || ''}
                  value={suppliers.find(s => s?.id === filterCriteria.supplierId) || null}
                  onChange={(_, newValue) => {
                    const newCriteria = {
                      ...filterCriteria,
                      supplierId: newValue?.id || undefined
                    };
                    setFilterCriteria(newCriteria);
                    onFilterChange(newCriteria);
                  }}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option =>
                      option?.name?.toLowerCase().includes(inputValue.toLowerCase()) ||
                      option?.id?.toLowerCase().includes(inputValue.toLowerCase())
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                />
              </Stack>

              {/* Show Drafts */}
              <FormControlLabel
                control={
                  <Switch
                    checked={filterCriteria.includeDraftPurchaseOrders || false}
                    onChange={(e) => {
                      const newCriteria = {
                        ...filterCriteria,
                        includeDraftPurchaseOrders: e.target.checked
                      };
                      setFilterCriteria(newCriteria);
                      onFilterChange(newCriteria);
                    }}
                  />
                }
                label="Include draft purchase orders"
              />

              <Divider />

              <Stack gap={1} direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">Hide available items after</Typography>

                {/* Available Days Ago */}
                <JSEditableInput
                  label="Available Days Ago"
                  type="number"
                  placeholder="0"
                  suffix="days"
                  value={filterCriteria.availableDaysAgo?.toString() || ''}
                  onValueChange={(value) => handleAvailableDaysAgoChange(value as string)}
                />
              </Stack>

            </Stack>
          </Box>

        </Paper>
      </Popover>
    </div >
  );
};

export default PurchaseOrderFilter;
